import React, { ReactElement } from "react";
import { useLocation } from "react-router";
import AdAside from "~/components/ads/AdAside";
import { getLocale } from "~/i18n";
import { useTranslation } from "react-i18next";

interface Props {
  children: React.ReactNode,
}

const AdditionalContentLayout: React.FC<Props> = (
  {
    children
  }
) => {
  const locale = getLocale(useLocation().pathname);

  const { t } = useTranslation("translations");

  /**
   * Collect links for contents section from AdditionalInfoSection components
   */
  const sections: Array<{ anchor: string, text: string, emoji: string }> = [];
  const additionalInfoComponent: ReactElement | null = React.Children.toArray(children)
    .find(child => React.isValidElement(child)
      && child.type
      // Using componentType here instead of name property because production minified version strips out component names
      && (child.type as any).componentType === "AdditionalInfo"
    ) as ReactElement | null;

  if (additionalInfoComponent) {
    const processChildren = (children: React.ReactNode) => {
      React.Children.toArray(children).forEach(child => {
        if (React.isValidElement(child)) {
          // Handle Fragment case
          if (child.type === React.Fragment) {
            processChildren(child.props.children);
            return;
          }

          if (child.props.id && child.props.contentsText) {
            sections.push({
              anchor: child.props.id,
              text: child.props.contentsText,
              emoji: child.props.contentsEmoji
            });
          }
        }
      });
    };

    processChildren(additionalInfoComponent.props.children);
  }

  function scrollToAnchor(event: React.MouseEvent<HTMLAnchorElement>) {
    event.preventDefault();
    const anchor = event.currentTarget.getAttribute("href")?.substring(1);
    if (!anchor) return;

    const element = document.getElementById(anchor);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
      // Update URL with the anchor without scrolling
      window.history.pushState({}, "", `#${anchor}`);
    }
  };

  return (
    <div className="w-full flex flex-col md:flex-row-reverse gap-6">
      <aside className="flex flex-col md:w-[250-px] lg:w-[300px] 2xl:w-[400px] shrink-0 md:sticky top-4 h-fit">
        {/* Table of Contents */}
        {sections.length > 0 && <>
          <div className="py-4">
            <p className="text-xl text-slate-900 dark:text-white font-semibold mb-2">
              {t("table_of_contents")}
            </p>
            <ul className="space-y-2">
              {sections.map(({ anchor, text, emoji }) => (
                <li key={anchor} className="grid grid-cols-[24px_1fr] items-center">
                  <span className="w-6 text-center">{emoji}</span>
                  <div>
                    <a
                      href={`#${anchor}`}
                      className="inline hover:text-primary-500 hover:border-primary-500 dark:hover:text-white dark:hover:border-white transition-colors font-normal"
                      onClick={scrollToAnchor}

                    >
                      {text}
                    </a>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </>}

        <div className="hidden md:flex top-4 flex-col gap-4">
          {locale === "ru"
            ? <AdAside type="yandex" adId="R-A-7013740-1" />
            : <AdAside type="google" adId="8042944538" />
          }
        </div>
      </aside>

      <div className="flex flex-col w-full items-start justify-start min-w-0">
        {children}
      </div>
    </div>
  );
};

export default AdditionalContentLayout;
